import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import _ from 'underscore';

import { useActions } from 'hooks/useActions';
import * as authActions from 'global/actions/authActions';
import { userRoles } from 'constants/users';
import { categories } from 'constants/tests';
import { Container } from 'components/Layout/Layout';
import TicketLinkModal from 'components/Modals/TicketModals/TicketLinkModal';
import { SpinnerWrapper } from 'components/Spinner/Spinner';
import { isValidEmail } from 'functions/stringValidation';
import { getCookie } from 'functions/getCookie';
import { getConfig } from 'functions/getConfig';
import Tour from 'components/Tour/Tour';
import { TOUR_DATA } from './SendTestTourData';
import * as actions from './actions';
import CandidatesList from './CandidatesList';
import SelectTestSection from './SelectTestSection';
import EmailSettings from './EmailSettings';
import ColleaguesListing from './ColleaguesListing';
import CandidateSettings from './CandidateSettings';
import { RectangleButton } from 'components/Button/RectangleButton';
import {
  BuyTestLink,
  DemoCompanyPurchaseSection,
  SendIcon,
  SendTestButtonContainer,
  SendTestFormContainer,
  SendTestPageWrapper,
  SendTestForm,
} from './styles';

export default function SendTestPage() {
  const localStorageInfo = JSON.parse(localStorage.getItem('fsSendTest'));

  const { loadCompanySendingTestSettings, loadTests, sendTicket, resetStateResponse } = useActions(actions);
  const { verifyAuthState } = useActions(authActions);

  const { userRole, department, company } = useSelector((state) => state.auth);
  let { loading, testLoading, error, companySettings, testsData, sendTestResult } = useSelector(
    (state) => state.SendTestPageReducer
  );

  const [tourCompleted, setTourCompleted] = useState(getCookie('fsTourSendTestDone'));
  const [tickets, setTickets] = useState([]);
  const [automatedEmail, setAutomatedEmail] = useState(localStorageInfo?.automatedEmail ?? true);
  const [letCandidateSeeScore, setLetCandidateSeeScore] = useState(localStorageInfo?.letCandidateSeeScore ?? true);
  const [selectedTests, setSelectedTests] = useState(localStorageInfo?.selectedTests ?? []);
  const [candidateList, setCandidateList] = useState(
    localStorageInfo?.candidateList ?? [{ email: '', name: '', isValidEmail: true, isValidName: true }]
  );
  const [colleaguesList, setColleaguesList] = useState(localStorageInfo?.filteredColleaguesList ?? []);
  const [deductCredits, setDeductCredits] = useState(localStorageInfo?.deductCredits ?? true);
  const [reminderEmail, setReminderEmail] = useState(localStorageInfo?.reminderEmail);
  const [reminderEmailDays, setReminderEmailDays] = useState(
    Number(localStorageInfo?.reminderEmailDays || companySettings?.company?.reminderEmailDays || 0)
  );
  const [emailSubject, setEmailSubject] = useState(localStorageInfo?.emailSubject ?? '');
  const [emailBody, setEmailBody] = useState(localStorageInfo?.emailBody ?? '');
  const [activeLanguageIsEnglish, setActiveLanguageIsEnglish] = useState(
    localStorageInfo?.activeLanguageIsEnglish ?? true
  );

  const isDemoCompany = company?.freeDemo ?? false;
  const isDemoDepartment = department?.demo ?? false;
  const userIsSuperAdmin = userRole === userRoles.SUPER_ADMIN;
  const maxReminderEmailInDays = (companySettings.company?.autoArchiveWeeks ?? 4) * 7 - 1;

  const selectTestSectionRef = useRef();
  const sendTestFormSectionRef = useRef();

  useEffect(() => {
    if (error && !error.includes('internal error')) toast.error(error);
  }, [error]);

  useEffect(() => {
    loadCompanySendingTestSettings();
  }, [loadCompanySendingTestSettings]);

  useEffect(() => {
    loadTests();
  }, [loadTests]);

  useEffect(() => {
    if (!companySettings.company) return;

    setColleaguesList((coll) => {
      let colleagues = companySettings?.teamMembers ?? [];
      let toBeAdded = [];
      for (let colleague of colleagues) {
        if (coll?.findIndex((c) => String(colleague._id) === String(c._id)) === -1) {
          toBeAdded.push(colleague);
        }
      }
      return [...coll, ...toBeAdded]?.slice().sort((a, b) => {
        return a?.name?.localeCompare(b.name);
      });
    });

    const emailSettings = companySettings?.emails?.find((o) => true);
    // If the values are not already set above in useState(localStorageInfo?.X)
    // use values from the companySettings api call
    setEmailSubject((emailSubject) => {
      return emailSubject ? emailSubject : emailSettings?.enSubject ?? '';
    });
    setEmailBody((emailBody) => (emailBody ? emailBody : emailSettings?.enHtml ?? ''));

    setReminderEmailDays((days) => Number(days || companySettings?.company?.reminderEmailDays || 0));
    setReminderEmail((enabled) =>
      typeof enabled === 'boolean' ? enabled : companySettings.company?.reminderEmailEnabled
    );
  }, [companySettings.teamMembers, companySettings.emails, companySettings.company]);

  const resetState = useCallback(() => {
    setReminderEmailDays(companySettings?.company?.reminderEmailDays ?? 6);
    setReminderEmail(companySettings?.company?.reminderEmailEnabled ?? true);
    setSelectedTests([]);
    setCandidateList([{ email: '', name: '', isValidEmail: true, isValidName: true }]);
    setColleaguesList(companySettings?.teamMembers ?? []);
    setDeductCredits(true);
    setLetCandidateSeeScore(true);
    setActiveLanguageIsEnglish(true);

    const emailSettings = companySettings?.emails?.find((o) => true);
    setEmailSubject(emailSettings?.enSubject ?? '');
    setEmailBody(emailSettings?.enHtml ?? '');
    localStorage.removeItem('fsSendTest');
  }, [companySettings]);

  useEffect(() => {
    if (_.keys(sendTestResult).length > 0) {
      setTickets(sendTestResult);
      resetState();
      verifyAuthState();
    }
  }, [sendTestResult, resetState, verifyAuthState]);

  useEffect(() => {
    const filteredColleaguesList =
      colleaguesList?.filter((c) => {
        return c.selected;
      }) ?? [];

    let dataToSave = localStorageInfo;

    if (company?._id) dataToSave = { ...dataToSave, companyId: company._id };

    if (reminderEmailDays) dataToSave = { ...dataToSave, reminderEmailDays };

    if (typeof reminderEmail === 'boolean') dataToSave = { ...dataToSave, reminderEmail };

    if (typeof deductCredits === 'boolean') dataToSave = { ...dataToSave, deductCredits };

    if (typeof letCandidateSeeScore === 'boolean') dataToSave = { ...dataToSave, letCandidateSeeScore };

    if (typeof automatedEmail === 'boolean') dataToSave = { ...dataToSave, automatedEmail };

    if (typeof activeLanguageIsEnglish === 'boolean') dataToSave = { ...dataToSave, activeLanguageIsEnglish };

    if (candidateList.length > 0) dataToSave = { ...dataToSave, candidateList };

    if (filteredColleaguesList) dataToSave = { ...dataToSave, filteredColleaguesList };

    if (selectedTests) dataToSave = { ...dataToSave, selectedTests };

    if (emailSubject) dataToSave = { ...dataToSave, emailSubject };

    if (emailBody) dataToSave = { ...dataToSave, emailBody };

    const emailSettings = companySettings?.emails?.find((o) => true);
    // if the emailSubject or emailBody is the same as the company settings, dont save it in localStorage.
    if (
      emailSubject &&
      emailSettings &&
      (emailSubject === emailSettings.enSubject || emailSubject === emailSettings.svSubject)
    )
      delete dataToSave.emailSubject;
    if (
      emailBody &&
      emailSettings &&
      (emailSettings?.enHtml.replace(/<p><\/p>/gi, '') === emailBody.replace(/<p><\/p>/gi, '') ||
        emailSettings?.svHtml.replace(/<p><\/p>/gi, '') === emailBody.replace(/<p><\/p>/gi, ''))
    )
      delete dataToSave.emailBody;

    if (
      localStorageInfo?.companyId !== undefined &&
      company?._id !== undefined &&
      localStorageInfo?.companyId !== company?._id
    ) {
      resetState();
    }

    if (Object.keys(dataToSave).length > 0) localStorage.setItem('fsSendTest', JSON.stringify(dataToSave));
  }, [
    selectedTests,
    letCandidateSeeScore,
    reminderEmail,
    reminderEmailDays,
    deductCredits,
    candidateList,
    colleaguesList,
    emailSubject,
    emailBody,
    automatedEmail,
    activeLanguageIsEnglish,
    company?._id,
    companySettings,
    resetState,
    localStorageInfo,
  ]);

  const showValidationError = (errorMessage, scrollToElementId = '') => {
    if (scrollToElementId !== '') {
      let elem = document.getElementById(scrollToElementId);
      if (elem) elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    toast.error(errorMessage);
  };

  const sendTestToCandidates = () => {
    let ticketData = { testObjects: [] };
    // Selected Tests time languages validations
    if (selectedTests.length === 0) {
      return showValidationError('Please select at least one test!', 'selectedTestsTable');
    }

    if (selectedTests.some((t) => (t?.selectedTime ?? 0) < 5))
      return showValidationError('Please set valid time limit for tests!', 'selectedTestsTable');

    for (let test of selectedTests) {
      let languagesAllowed = [];

      if (test.category === categories.CODE.category) {
        _.each(test.languages, (val, key) => {
          if (val) languagesAllowed.push(key);
        });
        if (languagesAllowed.length === 0) {
          return showValidationError('Please select language for tests!', 'selectedTestsTable');
        }
      }
      ticketData.testObjects.push({
        _id: test._id,
        duration: test.selectedTime, // ?? selectedTests[i].duration ?? 50,
        languagesAllowed: languagesAllowed,
        isPackage: test.category === 'Package',
      });
    }

    // Candidate Name and Email validations
    if (candidateList.length === 0) {
      return showValidationError('Please add candidates for the test!', 'candidatesSection');
    } else if (
      candidateList.some((candidate) => candidate.name === '' || (!isDemoDepartment && candidate.email === ''))
    ) {
      return showValidationError('Please fill Name and Email for candidates!', 'candidatesSection');
    } else if (
      candidateList.filter((candidate) => candidate.email !== '' && !isValidEmail(candidate.email)).length > 0
    ) {
      return showValidationError('Please input valid Email for candidates!', 'candidatesSection');
    }

    ticketData.candidates = candidateList.map((c) => {
      return { name: c.name, email: c.email };
    });

    if (reminderEmailDays > maxReminderEmailInDays || reminderEmailDays < 1)
      return showValidationError('Please a valid number of days for the reminder email!', 'candidatesSection');

    let emailSettings = {
      language: activeLanguageIsEnglish ? 'en' : 'sv',
      template: {
        svSubject: activeLanguageIsEnglish ? '' : emailSubject,
        svHtml: activeLanguageIsEnglish ? '' : emailBody,
        enSubject: activeLanguageIsEnglish ? emailSubject : '',
        enHtml: activeLanguageIsEnglish ? emailBody : '',
      },
    };
    // Email body validation for '[test-ticket-url]'
    if (automatedEmail && !emailSettings.template[`${emailSettings.language}Html`].includes('[test-ticket-url]')) {
      return showValidationError('Email body should contain [test-ticket-url]');
    }
    ticketData.emailSettings = emailSettings;

    const selectedReportReceiversIds = (
      colleaguesList?.filter((c) => {
        return c.selected;
      }) ?? []
    ).map((c) => c._id);
    ticketData.reportReceivers = selectedReportReceiversIds;
    ticketData.autoEmail = automatedEmail;
    ticketData.deductCredits = deductCredits;
    ticketData.showResult = letCandidateSeeScore;
    ticketData.reminderEmailDays = Number(reminderEmailDays);
    sendTicket(ticketData);
  };

  const toggleTour = () => {
    if (!tourCompleted) {
      document.cookie = `fsTourSendTestDone=true; Domain=${getConfig().COOKIE}; Max-Age=31536000; Path=/; SameSite=Lax`;
    }
    setTourCompleted(!tourCompleted);
  };

  return (
    <SpinnerWrapper loading={loading || testLoading}>
      <SendTestPageWrapper>
        <Container column full>
          {isDemoCompany && (
            <DemoCompanyPurchaseSection>
              Demo data - <BuyTestLink href="/plans">Buy a test</BuyTestLink> to active your account
            </DemoCompanyPurchaseSection>
          )}
          <SelectTestSection
            tests={testsData.tests ?? []}
            availableLanguages={testsData.availableLanguages ?? []}
            adaptiveTestInfo={companySettings.adaptiveTestInfo}
            isDemoCompany={isDemoCompany}
            toggleTour={toggleTour}
            selectedTests={selectedTests}
            setSelectedTests={setSelectedTests}
            ref={selectTestSectionRef}
          ></SelectTestSection>
          <SendTestFormContainer>
            <SendTestForm>
              <CandidatesList
                candidateList={candidateList}
                setCandidateList={setCandidateList}
                isDemoDepartment={isDemoDepartment}
              />
              <ColleaguesListing colleaguesList={colleaguesList} setColleaguesList={setColleaguesList} />
              <CandidateSettings
                letCandidateSeeScore={letCandidateSeeScore}
                setLetCandidateSeeScore={setLetCandidateSeeScore}
                reminderEmail={reminderEmail}
                setReminderEmail={setReminderEmail}
                reminderEmailDays={reminderEmailDays}
                setReminderEmailDays={setReminderEmailDays}
                deductCredits={deductCredits}
                setDeductCredits={setDeductCredits}
                userIsSuperAdmin={userIsSuperAdmin}
                automatedEmail={automatedEmail}
                setAutomatedEmail={setAutomatedEmail}
                maxReminderEmailInDays={maxReminderEmailInDays}
              />
              {automatedEmail && (
                <EmailSettings
                  emailSettings={companySettings.emails?.find((o) => true)}
                  emailSubject={emailSubject}
                  setEmailSubject={setEmailSubject}
                  emailBody={emailBody}
                  setEmailBody={setEmailBody}
                  activeLanguageIsEnglish={activeLanguageIsEnglish}
                  setActiveLanguageIsEnglish={setActiveLanguageIsEnglish}
                  ref={sendTestFormSectionRef}
                ></EmailSettings>
              )}
            </SendTestForm>
            <SendTestButtonContainer>
              <RectangleButton id="sendTestButton" onClick={() => sendTestToCandidates()}>
                SEND TESTS <SendIcon />
              </RectangleButton>
            </SendTestButtonContainer>
          </SendTestFormContainer>
        </Container>

        {!tourCompleted && <Tour tourData={TOUR_DATA} completed={tourCompleted} setCompleted={toggleTour} />}
        {tickets.length > 0 && (
          <TicketLinkModal
            tickets={tickets}
            closeModal={() => {
              setTickets([]);
              resetStateResponse();
            }}
          />
        )}
      </SendTestPageWrapper>
    </SpinnerWrapper>
  );
}
